import React, { useState, useEffect } from 'react';
import { User, Mail, Building2, Save, X, Edit2, ShieldCheck, Loader2 } from 'lucide-react';
import api from '../api/axios';

const customStyles = `
  @keyframes blob {
    0% { transform: translate(0px, 0px) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 20px) scale(0.9); }
    100% { transform: translate(0px, 0px) scale(1); }
  }
  .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
`;

const Profile = () => {
  const [userData, setUserData] = useState({ full_name: '', email: '', organization_name: '', mfa_enabled: false });
    const [editForm, setEditForm] = useState({ full_name: '', organization_name: '', mfa_enabled: false });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/users/me');
      setUserData(res.data);
      setEditForm({ 
    full_name: res.data.full_name || '', 
    organization_name: res.data.organization_name || '',
    mfa_enabled: res.data.mfa_enabled || false
    });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load profile data.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setMessage({ type: '', text: '' });
    try {
      // Assuming your backend has a PUT/PATCH endpoint to update the user
      // If not, you will need to create a quick FastAPI endpoint: @app.put("/users/me")
      const res = await api.put('/users/me', editForm);
      setUserData({ ...userData, ...editForm });
      setIsEditing(false);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      
      // Trigger a global refresh so the PageHeader updates the name/initials instantly
      window.dispatchEvent(new Event('app-refresh')); 
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditForm({ full_name: userData.full_name || '', organization_name: userData.organization_name || '' });
    setIsEditing(false);
    setMessage({ type: '', text: '' });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length > 1) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.substring(0, 2).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-[calc(100vh-5rem)] p-6 lg:p-12 relative overflow-hidden transition-colors duration-500">
      <style>{customStyles}</style>
      <div className="absolute top-[-10%] left-[10%] w-[40%] h-[40%] rounded-full bg-indigo-600/5 blur-[120px] animate-blob pointer-events-none"></div>

      <div className="max-w-3xl mx-auto relative z-10">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-wide text-slate-900 dark:text-white mb-2">My Profile</h1>
          <p className="text-xs tracking-wide font-bold text-slate-500 dark:text-slate-400">Manage your recruiter account settings</p>
        </div>

        {message.text && (
          <div className={`p-4 rounded-xl mb-6 text-sm font-medium border flex items-center gap-3 ${
            message.type === 'success' 
            ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20' 
            : 'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/20'
          }`}>
            {message.type === 'success' ? <ShieldCheck size={18} /> : <X size={18} />}
            {message.text}
          </div>
        )}

        <div className="bg-white dark:bg-[#0a0a0a]/50 backdrop-blur-md rounded-[2rem] border border-slate-200 dark:border-white/10 shadow-sm overflow-hidden">
          {/* Header Area */}
          <div className="px-8 py-10 bg-slate-50/50 dark:bg-white/5 border-b border-slate-200 dark:border-white/10 flex flex-col sm:flex-row items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-indigo-50 dark:bg-indigo-500/10 border-2 border-indigo-100 dark:border-indigo-500/20 flex items-center justify-center text-3xl font-light text-indigo-600 dark:text-indigo-400 shrink-0">
              {getInitials(userData.full_name)}
            </div>
            <div className="text-center sm:text-left flex-1">
              <h2 className="text-2xl font-medium dark:font-semibold text-slate-900 dark:text-white mb-1">{userData.full_name}</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-medium tracking-wide">{userData.email}</p>
            </div>
            {!isEditing && (
              <button 
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-white dark:bg-[#111] border border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold tracking-wide hover:border-indigo-500 hover:text-indigo-600 transition-all shadow-sm"
              >
                <Edit2 size={14} /> Edit Profile
              </button>
            )}
          </div>

          {/* Form Area */}
          <div className="p-8 sm:p-10 space-y-8">
            <div className="flex items-center justify-between p-5 bg-slate-50 dark:bg-[#111] border border-slate-200 dark:border-white/5 rounded-2xl transition-colors">
            <div>
                <h4 className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <ShieldCheck size={16} className="text-indigo-500" /> Multi-Factor Authentication
                </h4>
                <p className="text-[12px] text-slate-500 dark:text-slate-400 tracking-wide mt-1 font-medium">
                Require an email OTP when signing in
                </p>
            </div>
            
            <button
                type="button"
                disabled={!isEditing}
                onClick={() => setEditForm({...editForm, mfa_enabled: !editForm.mfa_enabled})}
                className={`w-12 h-6 rounded-full transition-colors flex items-center px-1 disabled:opacity-50 cursor-pointer ${
                (isEditing ? editForm.mfa_enabled : userData.mfa_enabled) 
                ? 'bg-emerald-500' 
                : 'bg-slate-300 dark:bg-slate-700'
                }`}
            >
                <div className={`w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${
                (isEditing ? editForm.mfa_enabled : userData.mfa_enabled) 
                ? 'translate-x-6' 
                : 'translate-x-0'
                }`}></div>
            </button>
            </div>
            {/* Read-Only Email */}
            <div>
              <label className="flex items-center gap-2 text-[12px] tracking-wide font-bold text-slate-500 dark:text-slate-400 mb-3">
                <Mail size={14} /> Email Address (Read-Only)
              </label>
              <input 
                type="email" 
                value={userData.email} 
                disabled 
                className="w-full bg-slate-100 dark:bg-black/40 border border-slate-200 dark:border-white/5 text-slate-500 dark:text-slate-500 px-4 py-3 rounded-xl text-sm placeholder:text-[12px] font-medium cursor-not-allowed"
              />
            </div>

            {/* Editable Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="flex items-center gap-2 text-[12px] tracking-wide font-bold text-slate-500 dark:text-slate-400 mb-3">
                  <User size={14} /> Full Name
                </label>
                <input 
                  type="text" 
                  value={isEditing ? editForm.full_name : userData.full_name}
                  onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 rounded-xl text-sm font-light outline-none transition-all ${
                    isEditing 
                    ? 'bg-white dark:bg-[#111] border-b-2 border-indigo-500 text-slate-900 dark:text-white shadow-sm' 
                    : 'bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/5 text-slate-700 dark:text-slate-300'
                  }`}
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-[12px] tracking-wide font-bold text-slate-500 dark:text-slate-400 mb-3">
                  <Building2 size={14} /> Organization Name
                </label>
                <input 
                  type="text" 
                  value={isEditing ? editForm.organization_name : userData.organization_name}
                  onChange={(e) => setEditForm({...editForm, organization_name: e.target.value})}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 rounded-xl text-sm font-light outline-none transition-all ${
                    isEditing 
                    ? 'bg-white dark:bg-[#111] border-b-2 border-indigo-500 text-slate-900 dark:text-white shadow-sm' 
                    : 'bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/5 text-slate-700 dark:text-slate-300'
                  }`}
                />
              </div>
            </div>

            {/* Actions */}
            {isEditing && (
              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-100 dark:border-white/5 animate-in slide-in-from-bottom-2 fade-in">
                <button 
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="px-6 py-2.5 text-xs font-bold tracking-wide text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSave}
                  disabled={isSaving || !editForm.full_name}
                  className="flex items-center gap-2 px-8 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold tracking-wide transition-all shadow-[0_0_15px_rgba(99,102,241,0.3)] active:scale-95 disabled:opacity-50"
                >
                  {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;